import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="modal"
// Manages native <dialog> elements for modals and slide-out panels
//
// Usage:
//   <button data-controller="modal"
//           data-action="click->modal#open"
//           data-modal-target-param="my_modal_id">
//     Open Modal
//   </button>
//
//   <dialog id="my_modal_id" class="modal modal-end"
//           data-controller="modal"
//           data-action="close->modal#handleClose">
//     ...
//   </dialog>

// Track open dialogs for stacking effect (CSS uses data-stack-depth and data-stack-size)
const dialogStack = []

function updateStackDepths() {
  dialogStack.forEach((dialog, index) => {
    dialog.dataset.stackDepth = index
    dialog.dataset.stackSize = dialogStack.length
  })
}

function addToStack(dialog) {
  if (!dialogStack.includes(dialog)) {
    dialogStack.push(dialog)
    updateStackDepths()
  }
}

function removeFromStack(dialog) {
  const index = dialogStack.indexOf(dialog)
  if (index > -1) {
    dialogStack.splice(index, 1)
    updateStackDepths()
  }
}

// MutationObserver to automatically track dialog open/close state.
// This allows any code (not just this controller) to call showModal() and
// have the dialog automatically added to the stack for the stacking effect.
// Also handles turbo-frame src restoration on open.
const dialogObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    if (mutation.attributeName === "open") {
      const dialog = mutation.target
      if (dialog.hasAttribute("open")) {
        addToStack(dialog)
        // Restore turbo-frame src and reload to trigger fresh load
        dialog.querySelectorAll("turbo-frame[data-original-src]").forEach(frame => {
          frame.src = frame.dataset.originalSrc
          // Use reload() if available to ensure fresh content
          if (typeof frame.reload === 'function') {
            frame.reload()
          }
        })
      } else {
        removeFromStack(dialog)
      }
    }
  }
})

export default class extends Controller {
  static values = {
    autoFocus: { type: Boolean, default: true },
    autoOpen: { type: Boolean, default: false },
    dismissible: { type: Boolean, default: true }
  }

  connect() {
    if (this.element instanceof HTMLDialogElement) {
      // Watch for open/close to update stack
      dialogObserver.observe(this.element, { attributes: true, attributeFilter: ["open"] })

      // If nested inside another dialog, move to body level
      // This prevents parent dialog transforms from affecting positioning
      const parentDialog = this.element.parentElement?.closest("dialog")
      if (parentDialog) {
        document.body.appendChild(this.element)
      }

      if (this.autoOpenValue && !this.element.open) {
        this.#showModal(this.element)
      }
    }
  }

  // Open a modal by its ID (pass via data-modal-target-param)
  // Optionally set turbo-frame src from data-base-url + data-params
  open(event) {
    const modal = document.getElementById(event.params.target)
    if (!(modal instanceof HTMLDialogElement)) return

    this.#setFrameUrl(modal, event.currentTarget)
    this.#showModal(modal)
  }

  #setFrameUrl(modal, trigger) {
    const baseUrl = trigger.dataset.baseUrl
    if (!baseUrl) return

    const url = new URL(baseUrl, window.location.origin)
    const paramsJson = trigger.dataset.params
    if (paramsJson) {
      const params = JSON.parse(paramsJson)
      for (const [name, values] of Object.entries(params)) {
        values.forEach(value => url.searchParams.append(name, value))
      }
    }

    const frame = modal.querySelector("turbo-frame")
    if (frame) {
      frame.src = url.toString()
      frame.dataset.originalSrc = url.toString()
    }
  }

  #showModal(modal) {
    modal.showModal()

    if (this.autoFocusValue) {
      requestAnimationFrame(() => {
        const firstInput = modal.querySelector('input:not([type="hidden"]), textarea, select')
        if (firstInput) firstInput.focus()
      })
    }
  }

  // Close the modal this controller is attached to
  close(event) {
    const dialog = this.element.closest("dialog") || this.element
    if (dialog instanceof HTMLDialogElement) {
      dialog.close()
    }
  }

  // Handle the native close event (fired when dialog closes)
  handleClose(event) {
    // Reset turbo frames inside the modal to allow re-loading
    this.element.querySelectorAll("turbo-frame").forEach(frame => {
      // Store the original src if not already stored (for backwards compatibility)
      if (frame.hasAttribute("src") && !frame.dataset.originalSrc) {
        frame.dataset.originalSrc = frame.src
      }
      frame.removeAttribute("src")
      frame.innerHTML = frame.dataset.loadingHtml || '<div class="flex justify-center py-8"><span class="loading loading-spinner loading-md"></span></div>'
    })
  }

  preventCancel(event) {
    if (!this.dismissibleValue) event.preventDefault()
  }

  // Close on escape key (native dialog handles this, but useful for custom implementations)
  closeOnEscape(event) {
    if (event.key === "Escape" && this.dismissibleValue) {
      this.close(event)
    }
  }

  // Prevent closing if form has unsaved changes
  confirmClose(event) {
    const form = this.element.querySelector("form")
    if (form && this.hasUnsavedChanges(form)) {
      if (!confirm("You have unsaved changes. Are you sure you want to close?")) {
        event.preventDefault()
      }
    }
  }

  hasUnsavedChanges(form) {
    // Check if any form field has been modified
    const inputs = form.querySelectorAll("input, textarea, select")
    for (const input of inputs) {
      if (input.type === "hidden") continue
      if (input.defaultValue !== undefined && input.value !== input.defaultValue) {
        return true
      }
    }
    return false
  }
}
